// New-UI app shell: Sidebar + TopHeader + <Outlet/>, replacing LC2's old MainLayout
// (src/layout/MainLayout/MainLayout.js), which had no left sidebar — same destinations
// lived as horizontal tabs (see Sidebar.js's header comment).
//
// LOGIC PORTED from LC2's MainLayout: the team-member session fetch, and the account/
// community/campaign fetch + permission-filtering pipeline that populates the `entities`
// and `dashboard` reducers the two dashboard tables (and the breadcrumb header) read from.
// Trimmed vs. the original: no residents/child-accounts fetch, no Intercom wiring, no
// settings/invoices routing side effects — add those back with their own pages.
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Outlet, useLocation, useParams} from 'react-router-dom';
import {Box} from '@mui/material';

import {getCookie} from '../../commons/utils/cookies';
import {useLazyGetTeamMemberInfoQuery} from '../../features/loyaltyCloudApi/apiSlice';
import {useLazyGetAccountsQuery} from '../../features/coreApi/apiSlice';
import {useLazyGetCommunitiesByAccountSfidQuery} from '../../features/coreApi/communitiesSlice';
import {useLazyGetCampaignsByAccountSfidQuery} from '../../features/actionsApi/campaignsSlice';
import {useGetResidentsByAccountSfidQuery} from '../../features/coreApi/residentsSlice';
import {getCampaigns, getCommunities, setAccounts, setCampaigns, setCommunities, setResidents} from '../../app/store/reducers/entities';
import {setDashboard, setCommunitiesSelected, setCampaignsSelected, getDashboardAccount} from '../../app/store/reducers/dashboard';
import {getTeamMemberRole} from '../../app/store/reducers/session';
import {activeItem} from '../../app/store/reducers/menu';
import {findUserAccountsPermission} from '../../commons/components/MenuDashboard/utils';

import Sidebar from './Sidebar';
import TopHeader from './TopHeader';

const MainLayout = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const location = useLocation();

    const sessionToken = getCookie('session_token');
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isAdministrator = teamMemberRole === 'Administrator';
    const dashboardAccount = useSelector(getDashboardAccount);
    const entitiesCommunities = useSelector(getCommunities);
    const entitiesCampaigns = useSelector(getCampaigns);

    const [fetchTeamMemberInfo, {data: teamMember, isSuccess: isTMSuccess, isFetching: isTMFetching, isLoading: isTMLoading}] =
        useLazyGetTeamMemberInfoQuery();
    const [fetchAccounts, {data: acResponse = [], isSuccess: isAcSuccess, isLoading: isAcLoading, isFetching: isAcFetching}] =
        useLazyGetAccountsQuery();
    const [
        fetchCommunities,
        {data: comResponse = [], isSuccess: isComSuccess, isLoading: isComLoading, isFetching: isComFetching, isError: isComError}
    ] = useLazyGetCommunitiesByAccountSfidQuery();
    const [fetchCampaigns, {data: camResponse = {}, isSuccess: isCamSuccess, isLoading: isCamLoading, isFetching: isCamFetching}] =
        useLazyGetCampaignsByAccountSfidQuery();
    const {
        data: resResponse = [],
        isSuccess: isResSuccess,
        isLoading: isResLoading,
        isFetching: isResFetching
    } = useGetResidentsByAccountSfidQuery(params?.account_sfid, {skip: !params?.account_sfid});

    const [updatedAccountsPermissions, setUpdatedAccountsPermissions] = useState();

    useEffect(() => {
        if (sessionToken) {
            fetchTeamMemberInfo(sessionToken);
        }
    }, [sessionToken, fetchTeamMemberInfo]);

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    // Ported from LC2's MainLayout: `openItem` reflects whether the current route is an
    // account-, community-, or campaign-level URL — EsgTab (and other tabs keyed off
    // `getOpenItem`) branch on this to decide account-level vs. community-level data.
    useEffect(() => {
        const path = location.pathname;
        if (path.includes('campaign')) {
            dispatch(activeItem('campaigns'));
        } else if (path.includes('community')) {
            dispatch(activeItem('communities'));
        } else {
            dispatch(activeItem('portfolio'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (params?.account_sfid) {
            localStorage.setItem('account_sfid', params.account_sfid);
        }
    }, [params]);

    useEffect(() => {
        if (params?.account_sfid) {
            fetchCommunities(params.account_sfid);
            fetchCampaigns(params.account_sfid);
        }
    }, [params?.account_sfid, fetchCommunities, fetchCampaigns]);

    const isAdmin = useMemo(
        () => teamMember?.role === 'Administrator' || teamMember?.property_permissions?.length === 0,
        [teamMember]
    );

    useEffect(() => {
        if (isAcSuccess && !isAcFetching && !isAcLoading && isTMSuccess && !isTMLoading && !isTMFetching) {
            const perms = findUserAccountsPermission(teamMember?.master, params?.account_sfid, teamMember?.additional_accounts_permissions, acResponse);
            setUpdatedAccountsPermissions(perms);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMember, params?.account_sfid, isAcFetching, isAcLoading, isAcSuccess, isTMSuccess, isTMLoading, isTMFetching]);

    // Account-level breadcrumb label + stat cards data source.
    useEffect(() => {
        if (acResponse.length > 0 && params?.account_sfid) {
            const selectedAccount = acResponse.find((account) => account.sfid === params.account_sfid);
            if (selectedAccount) {
                dispatch(setDashboard({type: 'portfolio', data: selectedAccount}));
            }
        }
    }, [acResponse, params?.account_sfid, dispatch]);

    // Account switcher list (Sidebar's AccountSwitcher) — same permission-filtered list
    // LC2's AccountDashboard.js built for its own account picker. This was missing
    // entirely before (entities.accounts was never populated), so the switcher had
    // nothing to show.
    useEffect(() => {
        if (updatedAccountsPermissions) {
            const selectedAccounts = acResponse.filter((acc) => updatedAccountsPermissions.includes(acc.sfid));
            dispatch(setAccounts(selectedAccounts));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedAccountsPermissions, acResponse]);

    // Same permission-filtering as LC2's MainLayout: master/admin see everything, everyone
    // else only the communities/campaigns their `property_permissions` cover. `entities`
    // (setCommunities) holds the full filtered list the tables/permission checks use;
    // `dashboard` (setCommunitiesSelected) holds the same list for the breadcrumb dropdown
    // — LC2 dispatched both together everywhere, so we do too.
    useEffect(() => {
        if (!isComFetching && !isComLoading && isComSuccess) {
            if (teamMember?.master || (updatedAccountsPermissions && updatedAccountsPermissions.length > 1)) {
                dispatch(setCommunities(comResponse));
                dispatch(setCommunitiesSelected(comResponse));
            } else {
                const propertyPermissions = teamMember?.property_permissions || [];
                if (isAdmin || isAdministrator) {
                    dispatch(setCommunities(comResponse));
                    dispatch(setCommunitiesSelected(comResponse));
                } else {
                    const filtered = propertyPermissions.filter((perm) => perm && perm !== 'all' && perm !== 'blank');
                    const selectedCommunities = comResponse.filter((com) => filtered?.includes(com.sfid));
                    dispatch(setCommunities(selectedCommunities));
                    dispatch(setCommunitiesSelected(selectedCommunities));
                }
            }
        } else if (isComError) {
            dispatch(setCommunities([]));
            dispatch(setCommunitiesSelected([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMember, isComFetching, isComLoading, isComSuccess, isComError, comResponse, updatedAccountsPermissions, isAdmin, isAdministrator]);

    useEffect(() => {
        if (!isCamFetching && !isCamLoading && isCamSuccess && camResponse?.result) {
            if (teamMember?.master) {
                dispatch(setCampaigns(camResponse.result));
            } else if (comResponse.length > 0) {
                const propertyPermissions = teamMember?.property_permissions || [];
                const filtered = isAdmin || isAdministrator ? comResponse.map((c) => c.sfid) : propertyPermissions.filter((p) => p && p !== 'all' && p !== 'blank');
                const communitiesSfids = comResponse.filter((c) => filtered.includes(c.sfid)).map((c) => c.sfid);
                dispatch(setCampaigns(camResponse.result.filter((camp) => communitiesSfids.includes(camp.property_sfid))));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMember, isCamFetching, isCamLoading, isCamSuccess, camResponse, comResponse, isAdmin, isAdministrator]);

    // Residents fetch + permission filtering — same as LC2's MainLayout, feeds
    // CampaignInfo's resident counts (getResidents from `entities`).
    useEffect(() => {
        if (!isResFetching && !isResLoading && isResSuccess) {
            if (teamMember?.master) {
                dispatch(setResidents(resResponse));
            } else if (comResponse.length > 0) {
                const propertyPermissions = teamMember?.property_permissions || [];
                const filtered = isAdmin || isAdministrator ? comResponse.map((c) => c.sfid) : propertyPermissions.filter((p) => p && p !== 'all' && p !== 'blank');
                const communitiesSfids = comResponse.filter((c) => filtered.includes(c.sfid)).map((c) => c.sfid);
                dispatch(setResidents(resResponse.filter((res) => communitiesSfids.includes(res.property__c))));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMember, isResFetching, isResLoading, isResSuccess, resResponse, comResponse, isAdmin, isAdministrator]);

    // Campaign dropdown options, scoped to the current community — same effect as LC2's
    // MainLayout (dashboard.campaigns via setCampaignsSelected, separate from the org-wide
    // entities.campaigns above).
    useEffect(() => {
        if (params?.community_sfid && entitiesCampaigns.length > 0) {
            dispatch(setCampaignsSelected(entitiesCampaigns.filter((camp) => camp.property_sfid === params.community_sfid)));
        } else if (entitiesCampaigns.length > 0) {
            dispatch(setCampaignsSelected(entitiesCampaigns));
        } else {
            dispatch(setCampaignsSelected([]));
        }
    }, [entitiesCampaigns, params?.community_sfid, dispatch]);

    // Community/campaign breadcrumb labels, mirroring LC2's community/campaign effects.
    useEffect(() => {
        if (params?.community_sfid && entitiesCommunities.length > 0) {
            const currentCommunity = entitiesCommunities.find((com) => com.sfid === params.community_sfid);
            if (currentCommunity) {
                dispatch(setDashboard({type: 'communities', data: {sfid: currentCommunity.sfid, name: currentCommunity.name}}));
            }
        } else {
            // No count suffix here (see TopHeader.js's allCommunitiesOption comment) — this
            // label and the header dropdown's pinned "All communities" option must read
            // identically, and a count here would come from a different, not-guaranteed-
            // to-match source (entities.communities) than the dropdown's searched list.
            dispatch(setDashboard({type: 'communities', data: {sfid: 'all', name: 'All communities'}}));
        }
    }, [params?.community_sfid, entitiesCommunities, dispatch]);

    useEffect(() => {
        if (params?.campaign_sfid && entitiesCampaigns.length > 0) {
            const currentCampaign = entitiesCampaigns.find((camp) => camp.sfid === params.campaign_sfid);
            if (currentCampaign) {
                dispatch(setDashboard({type: 'campaigns', data: {sfid: currentCampaign.sfid, name: currentCampaign.name}}));
            }
        } else {
            // No count suffix — same reasoning as the communities label above.
            dispatch(setDashboard({type: 'campaigns', data: {sfid: 'all', name: 'All campaigns'}}));
        }
    }, [params?.campaign_sfid, params?.community_sfid, entitiesCampaigns, dispatch]);

    return (
        <Box sx={{display: 'flex', width: '100%', minHeight: '100vh'}}>
            <Sidebar communityName={dashboardAccount?.name || 'Loyalty Cloud'} />
            <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column'}}>
                <TopHeader />
                <Box component="main" sx={{flex: 1, p: 3}}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default MainLayout;
